import { Component, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Column, SetupWizardDynamicTable } from '../../../setup-wizard-dynamic-table/setup-wizard-dynamic-table';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CommonModule } from '@angular/common';
import { BrowserService } from '../../../../../../../services/browser.service';
import { HeaderColumn } from '../../../setup-wizard-dynamic-table/header-column';
import { RowColumn } from '../../../setup-wizard-dynamic-table/row-column';
import {
	BeforeAfter,
	SECTION_CSV_LABELS,
	SetupWizardBooleanType,
	SetupWizardSection,
	SectionCsvKeys,
	SectionCsvTemplate,
	EMPTY_SECTION_CSV_TEMPLATE,
	SetupWizardPayload,
	LanguageCode,
	SetupWizardUser,
	TextUtils,
} from '@mira-avant/mtlibs';
import { SetupWizardBeforeAfter } from '../../setup-wizard-before-after/setup-wizard-before-after';
import { makeStatusColumn } from '../../setup-wizard-before-after-status/setup-wizard-before-after-status';
import { SetupWizard } from '../../../../../../../services/setup-wizard';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { MAX_FILTER_OPTIONS_ALLOWED } from '../../setup-wizard-edit-steps';
import { SetupWizardSectionUsersStatus } from './setup-wizard-section-users-status/setup-wizard-section-users-status';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { SetupWizardBeforeAfterStatusWithEnrollment } from '../../setup-wizard-before-after-status-with-enrollment/setup-wizard-before-after-status-with-enrollment';

//Column to house section details for each section
export function makeUsersColumn<T>(): Column {
	return {
		key: 'users',
		name: 'Users',
	};
}

export function makeEnrolledColumn<T>(table?: SetupWizardSectionsTable): Column {
	return {
		key: 'enrolled',
		name: 'Enrolled',
		sortOrder: null,
		// For sorting prefer the after.active property if it exists (is true or false)
		// and fall back to before.active if it is not present
		sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
			(a.after?.active ?? a.before?.active)
				?.toString()
				.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
		sortDirections: ['ascend', 'descend', null],
		filterMultiple: true,
		filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(value => ({
			text: value,
			value: value,
		})),
		filterFn: (list: string[], item: SetupWizardSection) => {
			if (!table) {
				return false;
			}

			const isEnrolled: boolean = table.isSelectedUserEnrolled(item);

			//List will contain a string of 'true' or 'false' or both
			return list.includes(isEnrolled.toString());
		},
	};
}

export function makeHistoricalColumn<T>(table?: SetupWizardSectionsTable): Column {
	return {
		key: 'historical',
		name: 'Historical',
		sortOrder: null,
		// For sorting prefer the after.active property if it exists (is true or false)
		// and fall back to before.active if it is not present
		sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
			(a.after?.active ?? a.before?.active)
				?.toString()
				.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
		sortDirections: ['ascend', 'descend', null],
		filterMultiple: true,
		filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(value => ({
			text: value,
			value: value,
		})),
		filterFn: (list: string[], item: SetupWizardSection) => {
			if (!table) {
				return false;
			}

			const isHistorical: boolean = table.isSelectedUserHistorical(item);

			//List will contain a string of 'true' or 'false' or both
			return list.includes(isHistorical.toString());
		},
	};
}

const FULL_WIDTH_BREAKPOINT = 1510;

@Component({
	selector: 'app-setup-wizard-sections-table',
	imports: [
		CommonModule,
		SetupWizardDynamicTable,
		NzIconModule,
		RowColumn,
		HeaderColumn,
		SetupWizardBeforeAfter,
		NzTooltipModule,
		NzSpinModule,
		SetupWizardSectionUsersStatus,
		NzButtonModule,
		NzPopconfirmModule,
		SetupWizardBeforeAfterStatusWithEnrollment,
	],
	standalone: true,
	templateUrl: './setup-wizard-sections-table.html',
	styleUrl: './setup-wizard-sections-table.scss',
})
export class SetupWizardSectionsTable {
	private clientUuid: string | undefined;
	private sub?: Subscription;
	private resizeSub?: Subscription;
	public loading = signal<boolean>(true);
	public pageSize = input<number>(10);
	public rows = signal<SetupWizardSection[]>([]);
	readonly rowType = null! as BeforeAfter<SectionCsvTemplate>; // type inference only
	public columns = signal<Column[]>([]);
	private wideColumns: Column[] = [];
	private narrowColumns: Column[] = [];
	public onEdit = output<SetupWizardSection>();
	public onEditUsers = output<SetupWizardSection>();
	public onToggleDisable = output<SetupWizardSection>();
	public onRemove = output<SetupWizardSection>();
	public onReset = output<SetupWizardSection>();
	public onEnroll = output<{ user: SetupWizardUser; section: SetupWizardSection }>();
	public onUnenroll = output<{ user: SetupWizardUser; section: SetupWizardSection }>();
	public enableEditButton = input<boolean>(true);
	public enableToggleDisableButton = input<boolean>(true);
	public enableRemoveButton = input<boolean>(true);
	public enableResetButton = input<boolean>(true);
	public enableEnrollButton = input<boolean>(false);
	public enableUnenrollButton = input<boolean>(false);
	public selectedRow = input<BeforeAfter<SectionCsvTemplate> | null>(null);
	public selectedUser = input<SetupWizardUser | null>(null);
	public columnsFilter = input<string[]>([]);
	public SectionCsvKeys = SectionCsvKeys;
	public SECTION_CSV_LABELS = SECTION_CSV_LABELS;
	public SetupWizardBooleanType = SetupWizardBooleanType;

	private ssoSectionIdFilterOptions: { text: string; value: string }[] = [];
	private ssoCourseIdFilterOptions: { text: string; value: string }[] = [];
	private nameFilterOptions: { text: string; value: string }[] = [];
	private displayOrderFilterOptions: { text: string; value: number }[] = [];
	private activeStartDateFilterOptions: { text: string; value: string }[] = [];
	private activeEndDateFilterOptions: { text: string; value: string }[] = [];
	private sectionCodeFilterOptions: { text: string; value: string }[] = [];
	private sectionUuidFilterOptions: { text: string; value: string }[] = [];
	private courseUuidFilterOptions: { text: string; value: string }[] = [];

	private ssoSectionIdColumn: Column | undefined;
	private ssoCourseIdColumn: Column | undefined;
	private nameColumn: Column | undefined;
	private languageColumn: Column | undefined;
	private displayOrderColumn: Column | undefined;
	private activeStartDateColumn: Column | undefined;
	private activeEndDateColumn: Column | undefined;
	private sectionCodeColumn: Column | undefined;
	private allowSelfRosteringColumn: Column | undefined;
	private sectionUuidColumn: Column | undefined; //this will actually be hidden in the UI
	private courseUuidColumn: Column | undefined; //this will actually be hidden in the UI
	private activeColumn: Column | undefined;

	constructor(
		private route: ActivatedRoute,
		private browserService: BrowserService,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	async ngOnInit() {
		await this.initTable();

		this.sub?.add(
			this.setupWizard.preview$.subscribe(payload => {
				this.loading.set(true);

				if (payload) {
					this.updateRows(payload);
				}

				this.loading.set(false);
			})
		);
	}

	public isSelectedUserEnrolled(section: SetupWizardSection): boolean {
		const sectionBeforeAfter = section.after ?? section.before;

		if (!sectionBeforeAfter) {
			return false;
		}

		//Test identifying if the user is enrolled in a particular section
		const selectedUser = this.selectedUser();

		if (!selectedUser) {
			return false;
		}

		const userBeforeAfter = selectedUser.after ?? selectedUser.before;

		if (!userBeforeAfter) {
			return false;
		}

		return this.setupWizard.preview$.value.payload.usersToSections.some((userToSection, index) => {
			const linkBeforeAfter = userToSection.after ?? userToSection.before;

			if (!linkBeforeAfter) {
				return false;
			}

			const doesLinkMatchSection =
				(TextUtils.isNonEmptyString(linkBeforeAfter.sso_section_id) &&
					TextUtils.isNonEmptyString(sectionBeforeAfter.sso_section_id) &&
					linkBeforeAfter.sso_section_id === sectionBeforeAfter.sso_section_id) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.section_uuid) &&
					TextUtils.isNonEmptyString(sectionBeforeAfter.section_uuid) &&
					linkBeforeAfter.section_uuid === sectionBeforeAfter.section_uuid);

			if (!doesLinkMatchSection) {
				return false;
			}

			const doesLinkMatchUser =
				(TextUtils.isNonEmptyString(linkBeforeAfter.school_id_or_email) &&
					TextUtils.isNonEmptyString(userBeforeAfter.school_id_or_email) &&
					linkBeforeAfter.school_id_or_email === userBeforeAfter.school_id_or_email) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.user_uuid) &&
					TextUtils.isNonEmptyString(userBeforeAfter.user_uuid) &&
					linkBeforeAfter.user_uuid === userBeforeAfter.user_uuid);

			return doesLinkMatchSection && doesLinkMatchUser && linkBeforeAfter.enrolled;
		});
	}

	// Has the selected user been enrolled in this section at any time in
	// the past or currently?
	public isSelectedUserHistorical(section: SetupWizardSection): boolean {
		const sectionBeforeAfter = section.after ?? section.before;

		if (!sectionBeforeAfter) {
			return false;
		}

		const selectedUser = this.selectedUser();

		if (!selectedUser) {
			return false;
		}

		const userBeforeAfter = selectedUser.after ?? selectedUser.before;

		if (!userBeforeAfter) {
			return false;
		}

		return this.setupWizard.preview$.value.payload.usersToSections.some((userToSection, index) => {
			const linkBeforeAfter = userToSection.after ?? userToSection.before;

			if (!linkBeforeAfter) {
				return false;
			}

			const doesLinkMatchSection =
				(TextUtils.isNonEmptyString(linkBeforeAfter.sso_section_id) &&
					TextUtils.isNonEmptyString(sectionBeforeAfter.sso_section_id) &&
					linkBeforeAfter.sso_section_id === sectionBeforeAfter.sso_section_id) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.section_uuid) &&
					TextUtils.isNonEmptyString(sectionBeforeAfter.section_uuid) &&
					linkBeforeAfter.section_uuid === sectionBeforeAfter.section_uuid);

			if (!doesLinkMatchSection) {
				return false;
			}

			const doesLinkMatchUser =
				(TextUtils.isNonEmptyString(linkBeforeAfter.school_id_or_email) &&
					TextUtils.isNonEmptyString(userBeforeAfter.school_id_or_email) &&
					linkBeforeAfter.school_id_or_email === userBeforeAfter.school_id_or_email) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.user_uuid) &&
					TextUtils.isNonEmptyString(userBeforeAfter.user_uuid) &&
					linkBeforeAfter.user_uuid === userBeforeAfter.user_uuid);

			return doesLinkMatchSection && doesLinkMatchUser;
		});
	}

	private updateRows(payload: SetupWizardPayload) {
		this.rows.set(payload.payload.sections);
		this.updateFilterOptions();
	}

	private updateFilterOptions(): void {
		const rows = this.rows();

		this.ssoSectionIdFilterOptions = [...new Set(rows.map(row => row.before?.sso_section_id || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.ssoCourseIdFilterOptions = [...new Set(rows.map(row => row.before?.sso_course_id || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.nameFilterOptions = [...new Set(rows.map(row => row.before?.name || ''))].sort().map(value => ({
			text: value,
			value: value,
		}));

		this.displayOrderFilterOptions = [...new Set(rows.map(row => row.before?.display_order || 0))]
			.sort()
			.map(value => ({
				text: value.toString(),
				value: value,
			}));

		this.activeStartDateFilterOptions = [...new Set(rows.map(row => row.before?.active_start_date || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.activeEndDateFilterOptions = [...new Set(rows.map(row => row.before?.active_end_date || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.sectionCodeFilterOptions = [...new Set(rows.map(row => row.before?.section_code || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.sectionUuidFilterOptions = [...new Set(rows.map(row => row.before?.section_uuid || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.courseUuidFilterOptions = [...new Set(rows.map(row => row.before?.course_uuid || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		if (this.ssoSectionIdFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.ssoSectionIdColumn!.filterFn = undefined;
			this.ssoSectionIdColumn!.filterOptions = undefined;
		} else {
			this.ssoSectionIdColumn!.filterOptions = this.ssoSectionIdFilterOptions;
			this.ssoSectionIdColumn!.filterFn = (list: string[], item: SetupWizardSection) =>
				list.some(name => item.before?.sso_section_id?.indexOf(name) !== -1);
		}

		if (this.ssoCourseIdFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.ssoCourseIdColumn!.filterFn = undefined;
			this.ssoCourseIdColumn!.filterOptions = undefined;
		} else {
			this.ssoCourseIdColumn!.filterOptions = this.ssoCourseIdFilterOptions;
			this.ssoCourseIdColumn!.filterFn = (list: string[], item: SetupWizardSection) =>
				list.some(name => item.before?.sso_course_id?.indexOf(name) !== -1);
		}

		if (this.nameFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.nameColumn!.filterFn = undefined;
			this.nameColumn!.filterOptions = undefined;
		} else {
			this.nameColumn!.filterOptions = this.nameFilterOptions;
			this.nameColumn!.filterFn = (list: string[], item: SetupWizardSection) =>
				list.some(name => item.before?.name?.indexOf(name) !== -1);
		}

		if (this.displayOrderFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.displayOrderColumn!.filterFn = undefined;
			this.displayOrderColumn!.filterOptions = undefined;
		} else {
			this.displayOrderColumn!.filterOptions = this.displayOrderFilterOptions;
			this.displayOrderColumn!.filterFn = (list: number[], item: SetupWizardSection) =>
				list.some(name => item.before?.display_order === name);
		}

		if (this.activeStartDateFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.activeStartDateColumn!.filterFn = undefined;
			this.activeStartDateColumn!.filterOptions = undefined;
		} else {
			this.activeStartDateColumn!.filterOptions = this.activeStartDateFilterOptions;
			this.activeStartDateColumn!.filterFn = (list: string[], item: SetupWizardSection) =>
				list.some(name => item.before?.active_start_date?.indexOf(name) !== -1);
		}

		if (this.activeEndDateFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.activeEndDateColumn!.filterFn = undefined;
			this.activeEndDateColumn!.filterOptions = undefined;
		} else {
			this.activeEndDateColumn!.filterOptions = this.activeEndDateFilterOptions;
			this.activeEndDateColumn!.filterFn = (list: string[], item: SetupWizardSection) =>
				list.some(name => item.before?.active_end_date?.indexOf(name) !== -1);
		}

		if (this.sectionCodeFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.sectionCodeColumn!.filterFn = undefined;
			this.sectionCodeColumn!.filterOptions = undefined;
		} else {
			this.sectionCodeColumn!.filterOptions = this.sectionCodeFilterOptions;
			this.sectionCodeColumn!.filterFn = (list: string[], item: SetupWizardSection) =>
				list.some(name => item.before?.section_code?.indexOf(name) !== -1);
		}

		if (this.sectionUuidFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.sectionUuidColumn!.filterFn = undefined;
			this.sectionUuidColumn!.filterOptions = undefined;
		} else {
			this.sectionUuidColumn!.filterOptions = this.sectionUuidFilterOptions;
			this.sectionUuidColumn!.filterFn = (list: string[], item: SetupWizardSection) =>
				list.some(name => item.before?.section_uuid?.indexOf(name) !== -1);
		}

		if (this.courseUuidFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.courseUuidColumn!.filterFn = undefined;
			this.courseUuidColumn!.filterOptions = undefined;
		} else {
			this.courseUuidColumn!.filterOptions = this.courseUuidFilterOptions;
			this.courseUuidColumn!.filterFn = (list: string[], item: SetupWizardSection) =>
				list.some(name => item.before?.course_uuid?.indexOf(name) !== -1);
		}
	}

	public async initTable(): Promise<void> {
		try {
			this.initColumns();

			this.resizeSub?.unsubscribe();
			this.resizeSub = this.browserService.onResize$.subscribe((width: number) => {
				if (width >= FULL_WIDTH_BREAKPOINT) {
					this.columns.set(this.wideColumns);
				} else {
					this.columns.set(this.narrowColumns);
				}
			});
		} catch (error) {
			console.error('Error loading data:', error);
		}
	}

	private initColumns(): void {
		Object.keys(EMPTY_SECTION_CSV_TEMPLATE).map(key => {
			switch (key) {
				case SectionCsvKeys.ssoSectionId:
					this.ssoSectionIdColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.ssoSectionId],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							a.before?.sso_section_id!.localeCompare(b.before?.sso_section_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case SectionCsvKeys.ssoCourseId:
					this.ssoCourseIdColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.ssoCourseId],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							a.before?.sso_course_id!.localeCompare(b.before?.sso_course_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case SectionCsvKeys.name:
					this.nameColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.name],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							a.before?.name!.localeCompare(b.before?.name!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case SectionCsvKeys.language:
					this.languageColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.language],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							a.before?.language!.localeCompare(b.before?.language!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: Object.values(LanguageCode).map(code => ({ text: code, value: code })),
						filterFn: (list: string[], item: SetupWizardSection) =>
							list.some(name => item.before?.language?.indexOf(name) !== -1),
					};
					break;
				case SectionCsvKeys.displayOrder:
					this.displayOrderColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.displayOrder],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							(a.before?.display_order ?? -1) - (b.before?.display_order ?? -1),
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case SectionCsvKeys.activeStartDate:
					this.activeStartDateColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.activeStartDate],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							(a.before?.active_start_date ?? '').localeCompare(b.before?.active_start_date ?? '') ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case SectionCsvKeys.activeEndDate:
					this.activeEndDateColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.activeEndDate],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							(a.before?.active_end_date ?? '').localeCompare(b.before?.active_end_date ?? '') ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case SectionCsvKeys.sectionCode:
					this.sectionCodeColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.sectionCode],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							a.before?.section_code!.localeCompare(b.before?.section_code!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case SectionCsvKeys.allowSelfRostering:
					this.allowSelfRosteringColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.allowSelfRostering],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							(a.after?.allow_self_rostering ?? a.before?.allow_self_rostering)
								?.toString()
								.localeCompare(
									(b.after?.allow_self_rostering ?? b.before?.allow_self_rostering)?.toString()!
								) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case SectionCsvKeys.sectionUuid:
					this.sectionUuidColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.sectionUuid],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							a.before?.section_uuid!.localeCompare(b.before?.section_uuid!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: this.sectionUuidFilterOptions,
						filterFn: (list: string[], item: SetupWizardSection) =>
							list.some(name => item.before?.section_uuid?.indexOf(name) !== -1),
					};
					break;
				case SectionCsvKeys.courseUuid:
					this.courseUuidColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.courseUuid],
						sortOrder: null,
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							a.before?.course_uuid!.localeCompare(b.before?.course_uuid!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case SectionCsvKeys.active:
					this.activeColumn = {
						key,
						name: SECTION_CSV_LABELS[SectionCsvKeys.active],
						sortOrder: null,
						// For sorting prefer the after.active property if it exists (is true or false)
						// and fall back to before.active if it is not present
						sortFn: (a: SetupWizardSection, b: SetupWizardSection) =>
							(a.after?.active ?? a.before?.active)
								?.toString()
								.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(
							value => ({
								text: value,
								value: value,
							})
						),
						filterFn: (list: string[], item: SetupWizardSection) =>
							list.some(
								name => (item.after?.active ?? item.before?.active)?.toString()?.indexOf(name) !== -1
							),
					};
					break;
				default:
					throw new Error(`Column key not defined: ${key}`);
			}
		});

		if (
			!this.ssoSectionIdColumn ||
			!this.ssoCourseIdColumn ||
			!this.nameColumn ||
			!this.languageColumn ||
			!this.displayOrderColumn ||
			!this.activeStartDateColumn ||
			!this.activeEndDateColumn ||
			!this.sectionCodeColumn ||
			!this.allowSelfRosteringColumn ||
			!this.sectionUuidColumn ||
			!this.courseUuidColumn ||
			!this.activeColumn
		) {
			throw new Error('One or more columns are not defined');
		}

		const statusColumn: Column = makeStatusColumn<SetupWizardSection>();

		const sectionsColumn: Column = makeUsersColumn<SetupWizardSection>();

		this.wideColumns = [
			this.ssoSectionIdColumn,
			this.ssoCourseIdColumn,
			this.nameColumn,
			this.languageColumn,
			this.displayOrderColumn,
			this.activeStartDateColumn,
			this.activeEndDateColumn,
			this.sectionCodeColumn,
			this.allowSelfRosteringColumn,
			this.activeColumn,
			sectionsColumn,
			statusColumn,
		];

		// If the caller wants the enrolled column
		if (this.columnsFilter().length > 0 && this.columnsFilter().includes(makeEnrolledColumn().key)) {
			this.wideColumns.push(makeEnrolledColumn(this));
		}

		// If the caller wants the historical column
		if (this.columnsFilter().length > 0 && this.columnsFilter().includes(makeHistoricalColumn().key)) {
			this.wideColumns.push(makeHistoricalColumn(this));
		}

		this.wideColumns = this.wideColumns.filter(column =>
			this.columnsFilter().length > 0 ? this.columnsFilter().includes(column.key) : true
		);

		this.narrowColumns = [
			this.ssoSectionIdColumn,
			this.ssoCourseIdColumn,
			this.nameColumn,
			this.sectionCodeColumn,
			this.activeColumn,
		].filter(column => (this.columnsFilter().length > 0 ? this.columnsFilter().includes(column.key) : true));

		this.columns.set(this.wideColumns);
	}

	onEditClick(row: SetupWizardSection) {
		this.onEdit.emit(row);
	}

	onEditUsersClick(row: SetupWizardSection) {
		this.onEditUsers.emit(row);
	}

	onToggleDisableClick(row: SetupWizardSection) {
		this.onToggleDisable.emit(row);
	}

	onRemoveClick(row: SetupWizardSection) {
		this.onRemove.emit(row);
	}

	onResetClick(row: SetupWizardSection) {
		this.onReset.emit(row);
	}

	onEnrollClick(row: SetupWizardSection) {
		const user = this.selectedUser();
		if (!user) {
			throw new Error('No user selected for enrollment');
		}

		this.onEnroll.emit({ user, section: row });
	}

	onUnenrollClick(row: SetupWizardSection) {
		const user = this.selectedUser();
		if (!user) {
			throw new Error('No user selected for enrollment');
		}

		this.onUnenroll.emit({ user, section: row });
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
		this.resizeSub?.unsubscribe();
	}
}
